import { useFieldArray, useFormContext } from 'react-hook-form';
import { faPlus, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';

import { Schema } from '@typings';
import { getNestedValue } from '@utils';
import { isSchemaField } from '@utils/guards';

import { Button, Icon, Render } from '@components/Shared';

import { SchemaAppIntegrations } from '../SchemaAppIntegrations';

import { SchemaCollapsible } from './SchemaCollapsible';
import { SchemaNode } from './SchemaNode';

const copySchema = (schema: Schema.Model): Schema.Model =>
  JSON.parse(JSON.stringify(schema));

type ArrayItem = {
  id: string;
};

type Props = {
  schema: Schema.Array;
};

export const SchemaArray: React.FC<Props> = ({
  schema,
  schema: { title, description, name, children, isCollapsibleField },
}) => {
  const { control, formState } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const updateModelName = (model: Schema.Model, index: number) => {
    const schema = copySchema(model);

    if (isSchemaField(schema)) {
      schema.properties.name = schema.properties.name.replace(
        `${name}.$index`,
        `${name}.${index}`,
      );
    } else {
      schema.name = schema.name.replace(`${name}.$index`, `${name}.${index}`);
      schema.children = schema.children.map((schema) =>
        updateModelName(schema, index),
      );
    }

    return schema;
  };

  const makeItem = ({ id }: ArrayItem, index: number) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const normalizedNodes = children.map((schema) =>
      updateModelName(schema, index),
    );

    return (
      <div
        key={id}
        className="flex flex-col gap-6 rounded-xl border border-neural-02 p-6"
      >
        {normalizedNodes.map((schema) => (
          <SchemaNode key={`${schema.name}.${id}`} schema={schema} />
        ))}

        <div className="flex justify-end">
          <Button
            variant="ghost-error"
            icon={faTrashAlt}
            className="capitalize"
            onClick={() => remove(index)}
          >
            Delete entry
          </Button>
        </div>
      </div>
    );
  };

  const content = (
    <>
      <SchemaAppIntegrations schema={schema} />

      <Render if={fields}>
        <div className="mt-4 flex flex-col gap-4">{fields.map(makeItem)}</div>
      </Render>

      <div className="mt-4 flex justify-end">
        <Button className="capitalize" onClick={() => append('')}>
          <Icon icon={faPlus} />
          Add entry
        </Button>
      </div>
    </>
  );

  if (isCollapsibleField) {
    return (
      <SchemaCollapsible
        title={title}
        description={description}
        // TODO: does not work types validation
        hasError={!!getNestedValue(formState.errors, name)}
      >
        {content}
      </SchemaCollapsible>
    );
  }

  return (
    <div className="mt-6">
      <p className="flex items-start text-h6">{title}</p>

      <Render if={description}>
        <p className="mt-0.5 text-footnote text-neural-04">{description}</p>
      </Render>

      {content}
    </div>
  );
};
